/**
 * `/xizmatlar/:code` — xizmat haqida batafsil (ЧТЗ 4.2.9, 4.2.11.5).
 *
 * Kim foydalanishi mumkin, ko'rsatuvchi tashkilot va tizim, kerakli
 * ma'lumotlar, jarayon bosqichlari, natija, muddat va to'lov shartlari, yordam.
 * Maqsad (xizmat turi) tanlansa — «Mos maydonlarni xaritada ko'rish» va
 * shlyuz tugmalari shu maqsad bilan ishlaydi.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { usePortalConfig } from "@/shared/data/portal";
import { findService, serviceMapHref } from "@/shared/data/serviceCatalog";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { Icon } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";

import { SectionPage } from "./SectionPages";
import { ServiceActionButton, useServiceLauncher } from "./landing/ServiceGateway";
import { SplitHead } from "./landing/SplitHead";

function Block({ icon, title, children }: { icon: IconName; title: string; children: ReactNode }) {
  return (
    <section className="service-block">
      <h3>
        <span className="service-block-icon" aria-hidden="true">
          <Icon name={icon} size={16} />
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

export function ServiceDetailPage() {
  const { code = "" } = useParams();
  const service = findService(code);
  const config = usePortalConfig();
  const launcher = useServiceLauncher();
  const [purpose, setPurpose] = useState("");

  if (!service) return <Navigate to={SECTION_PATHS.services} replace />;
  const hasMap = Boolean(service.mapLayers?.length);
  const actions = service.actions.filter((action) => action.kind !== "map");

  return (
    <SectionPage revealKey={service.code}>
      <section className="section section-alt" id="service-detail" aria-labelledby="service-title">
        <div className="container">
          <SplitHead
            scene="services"
            eyebrow={
              <Link to={SECTION_PATHS.services} className="service-back">
                <Icon name="chevronRight" size={14} /> Elektron xizmatlar
              </Link>
            }
            title={service.title}
            titleId="service-title"
            lead={service.summary}
          />

          <div className="service-detail" data-reveal>
            <div className="service-detail-main">
              {service.purposes?.length ? (
                <Block icon="filter" title={service.purposesTitle ?? "Xizmat turi"}>
                  <div className="service-purposes" role="radiogroup" aria-label={service.purposesTitle}>
                    {service.purposes.map((item) => (
                      <label key={item.code} className={`service-purpose${purpose === item.code ? " is-selected" : ""}`}>
                        <input
                          type="radio"
                          name="service-purpose"
                          value={item.code}
                          checked={purpose === item.code}
                          onChange={() => setPurpose(item.code)}
                        />
                        <b>{item.title}</b>
                        <small>{item.text}</small>
                      </label>
                    ))}
                  </div>
                  <p className="hint">Maqsadni tanlasangiz, xaritada va xizmatga o'tishda shu maqsad hisobga olinadi.</p>
                </Block>
              ) : null}

              <Block icon="users" title="Kim foydalanishi mumkin">
                <ul className="service-list">
                  {service.audience.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Block>

              <Block icon="building" title="Xizmatni ko'rsatuvchi tashkilot va tizim">
                <dl className="service-kv">
                  <div>
                    <dt>Tashkilot</dt>
                    <dd>{service.provider.organization}</dd>
                  </div>
                  <div>
                    <dt>Tizim</dt>
                    <dd>
                      {service.provider.system}
                      {service.provider.url ? (
                        <span className="service-host num">{service.provider.url.replace(/^https?:\/\//, "")}</span>
                      ) : null}
                    </dd>
                  </div>
                  <div>
                    <dt>Kirish</dt>
                    <dd>{service.access}</dd>
                  </div>
                </dl>
              </Block>

              <Block icon="file" title="Kerakli ma'lumotlar va hujjatlar">
                <ul className="service-list">
                  {service.requirements.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Block>

              <Block icon="activity" title="Jarayon bosqichlari">
                <ol className="service-steps">
                  {service.steps.map((item, index) => (
                    <li key={item}>
                      <span className="service-step-num num" aria-hidden="true">{index + 1}</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              </Block>

              <Block icon="checkCircle" title="Natijada olinadigan hujjat">
                <p>{service.result}</p>
              </Block>

              <Block icon="clock" title="Muddat va to'lov shartlari">
                <dl className="service-kv">
                  {service.terms.map((term) => (
                    <div key={term.label}>
                      <dt>{term.label}</dt>
                      <dd>
                        {term.value}
                        <span className="service-source">Manba: {term.source}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
              </Block>
            </div>

            <aside className="service-detail-side">
              <div className="service-side-card">
                <h3>Xizmatdan foydalanish</h3>
                <p className="hint">{service.howItWorks}</p>
                {launcher.error ? (
                  <p className="alert alert-error" role="alert">
                    <Icon name="alert" size={18} />
                    <span>{launcher.error}</span>
                  </p>
                ) : null}
                <div className="service-side-actions">
                  {actions.map((action, index) => (
                    <ServiceActionButton
                      key={action.label}
                      service={service}
                      action={action}
                      primary={index === 0}
                      launcher={launcher}
                      purpose={purpose}
                    />
                  ))}
                  {hasMap ? (
                    <Link className="btn btn-outline btn-sm" to={serviceMapHref(service, purpose)}>
                      <Icon name="map" size={16} />
                      <span>Mos maydonlarni xaritada ko'rish</span>
                    </Link>
                  ) : null}
                </div>
                <p className="service-side-note">
                  <Icon name="lock" size={12} /> {service.access}
                </p>
              </div>

              <div className="service-side-card">
                <h3>Yordam va yo'riqnoma</h3>
                <ul className="service-help">
                  {service.help.map((item) => (
                    <li key={item.to}>
                      <Link to={item.to}>
                        {item.label}
                        <Icon name="arrowRight" size={14} />
                      </Link>
                    </li>
                  ))}
                  <li>
                    <a href={`tel:${config.hotline}`}>
                      Ishonch telefoni: {config.hotline}
                      <Icon name="phone" size={14} />
                    </a>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
      {launcher.dialog}
    </SectionPage>
  );
}
